import * as THREE from 'three';
import HullComponent from './hull_component.js';
import * as materials from './materials.js';

export default class Sublight extends HullComponent {
  constructor({name}) {
    super();

    this.group = new THREE.Group();
    this.dimensions = {};

    this.sublightGeometry = {};
    this.grillGeometry = {};

    this.sublightMesh = {};
    this.grillMesh = {};

    this.name = name;

    return this;

  }

  update({ 
    length,
    width,
    widthRatio,
    rotation=0,
    segments=4,
    skewVertical=0,
    skewHorizontal=0,
    materialIndex=0,
    undercutStart=0.0,
    undercut=0.0,
  }) {
    
    this.clear();
    const phiStart = segments == 3 ? Math.PI / 2 : Math.PI / segments;
    
    const worldPos = new THREE.Vector3();
    this.group.getWorldPosition(worldPos);
    this.rotationSense = worldPos.x > 0 ? 1 : -1;

    const wallThickness = 0.15;

    // sublight body
    
    var sublightPointCount = 40.0;
    var r = width;
    var sublightPoints = [
      new THREE.Vector2( r * (1.0 - wallThickness), 0.0 )
    ];

    for ( var i = 0; i <= sublightPointCount; i++ ) {  
      r = width;
      sublightPoints.push(
        new THREE.Vector2(
          r,
          i / sublightPointCount * length
        )
      );
    }
    sublightPoints.push( new THREE.Vector2( 0.0, length*1.1 ) );


    // grill
    var grillPointCount = 5.0;
    var grillPoints = [];
    // grillPoints.push( new THREE.Vector2( 0, 0 ) );
    for ( var i = grillPointCount; i >= 0; i-- ) {
      grillPoints.push(
        new THREE.Vector2(
          Math.pow(i / grillPointCount, 0.5) * width * (1.0 - wallThickness), //radius
          (5 - i / grillPointCount) * width * 0.04
        )
      );
    }

    // grill inner
    var grillInnerPoints = [];
    // grillInnerPoints.push( new THREE.Vector2( 0, 0 ) );
    for ( var i = grillPointCount; i >= 0; i-- ) {
      grillInnerPoints.push(
        new THREE.Vector2(
          Math.pow((i / grillPointCount), 0.5) * width * (1.0 - wallThickness),
          (6 - i / grillPointCount) * width * 0.04
        )
      );
    }

    this.sublightGeometry = new THREE.LatheGeometry(sublightPoints, segments, this.rotationSense * phiStart);
  
    const positions = this.sublightGeometry.attributes.position.array;

    this.sublightGeometry.scale(widthRatio, 1.0, 1.0);
    this.sublightGeometry.rotateY(rotation);

    this.grillGeometry = new THREE.LatheGeometry(grillPoints, segments, this.rotationSense * phiStart);
    this.grillGeometry.scale(widthRatio, 1.0, 1.0);
    this.grillGeometry.rotateY(rotation);

    this.grillInnerGeometry = new THREE.LatheGeometry(grillInnerPoints, segments, this.rotationSense * phiStart);
    this.grillInnerGeometry.scale(widthRatio, 1.0, 1.0);
    this.grillInnerGeometry.rotateY(rotation);

    this.sublightMesh = new THREE.Mesh( this.sublightGeometry, materials.pylonMaterial[materialIndex].clone() );
    this.sublightMesh.name = this.name + '_sublight';
    this.grillMesh = new THREE.Mesh( this.grillGeometry, materials.grillMaterial[materialIndex].clone() );
    this.grillMesh.name = this.name + '_grill';
    this.grillInnerMaterial = materials.grillInnerMaterial[materialIndex].clone();
    if (this.grillInnerMaterial.emissiveMap) {
      this.grillInnerMaterial.emissiveMap = materials.grillInnerMaterial[materialIndex].emissiveMap.clone();
    };
    this.grillInnerMesh = new THREE.Mesh( this.grillInnerGeometry, this.grillInnerMaterial);
    
    // skew not currently exposed to controls
    const Szy = skewVertical;
    const Szx = skewHorizontal;
    const matrix = new THREE.Matrix4();
    matrix.set(   1,     0,  0,  0,
                Szx,     1,  Szy,  0,
                  0,     0,   1,   0,
                  0,     0,   0,   1  );
    this.sublightGeometry.applyMatrix4( matrix );
    this.grillGeometry.applyMatrix4( matrix );
    this.grillInnerGeometry.applyMatrix4( matrix );

    // The starboard sublight is built as the mirror of the port one (negated
    // phiStart, rotation and skewHorizontal), but LatheGeometry's u sweep does
    // not mirror along with the geometry. Without flipping u the skin samples
    // the texture shifted by one segment instead of as a true mirror image.
    if (this.rotationSense < 0) {
      this.mirrorU(this.sublightGeometry);
      this.mirrorU(this.grillGeometry);
      this.mirrorU(this.grillInnerGeometry);
    }

    this.group.add( this.sublightMesh );
    this.group.add( this.grillMesh );
    this.group.add( this.grillInnerMesh );

    this.computeBoundingBox(this.sublightGeometry);
  }

  computeBoundingBox(measuredGeom){
    measuredGeom.computeBoundingBox();
    this.dimensions.x = measuredGeom.boundingBox.max.x - measuredGeom.boundingBox.min.x;
    this.dimensions.y = measuredGeom.boundingBox.max.y - measuredGeom.boundingBox.min.y;
    this.dimensions.z = measuredGeom.boundingBox.max.z - measuredGeom.boundingBox.min.z;
  }

  rotateGrill(rotation){
    rotation = rotation * 0.05;
    if (this.grillInnerMaterial.emissiveMap) { 
      this.grillInnerMaterial.emissiveMap.offset.x = this.rotationSense * rotation * 0.5;
      this.grillInnerMaterial.emissiveMap.offset.y = rotation;
      // this.grillInnerMaterial.emissiveMap.rotation = -this.rotationSense * rotation*0.1;
    };
  }

  setColor(color){
    this.sublightMesh.material.color.set(color);
  }

  setEmissive(color){
    // this.grillInnerMesh.material.emissive.set(color);
    // this.grillInnerMesh.material.color.set(color);
    this.grillMesh.material.emissive.set(color);
    this.grillMesh.material.color.set(color);
  }

  clear(){
    if (this.sublightGeometry['dispose']) {
      this.sublightGeometry.dispose();
      this.grillGeometry.dispose();
      this.grillInnerGeometry.dispose();
      for (var i = this.group.children.length - 1; i >= 0; i--) {
        this.group.remove(this.group.children[i]);
      }
    }
  }
}